import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Factory, Search, RotateCcw, FileSpreadsheet, Loader2, ArrowUp, ArrowDown, ArrowUpDown, RefreshCw,
  ListChecks, ClipboardList, Truck, Timer, AlertTriangle, Hourglass,
} from "lucide-react";
import { fmtDate, fmtQty } from "@/lib/dcGinReportExport";

// Pending Production Orders — Reports → Pending Production Orders. Read-only:
// one flat list computed live by the backend (GET /api/reports/pending-production,
// see backend/pending_production_report.py) from three sources, each tagged
// with an Alert Type:
//   * Production Pending     — open Work Orders not yet fully at Finished Goods
//   * Job Work Overdue       — Job Work DCs (Send Outside) from Machine Shop /
//                              Impregnation / Fabrication / Powder Coating not
//                              received back on a GIN within 5 days of the DC date
//   * Stage Movement Delayed — batches issued to a stage and not moved on to the
//                              next stage / Finished Goods within 3 days of issue
// Rows pending more than 3 days are highlighted amber, more than 7 days red.

const REFRESH_MS = 60_000;
const HIGHLIGHT_DAYS = 3;

export const ALERT_PRODUCTION = "Production Pending";
export const ALERT_JOB_WORK = "Job Work Overdue";
export const ALERT_STAGE = "Stage Movement Delayed";
const ALERT_TYPES = [ALERT_PRODUCTION, ALERT_JOB_WORK, ALERT_STAGE];

const ALERT_TONE = {
  [ALERT_PRODUCTION]: "bg-blue-50 text-blue-700",
  [ALERT_JOB_WORK]: "bg-orange-50 text-orange-700",
  [ALERT_STAGE]: "bg-violet-50 text-violet-700",
};

export const PPO_COLUMNS = [
  { field: "alert_type", header: "Alert Type" },
  { field: "ref_code", header: "Job / WO No." },
  { field: "dc_no", header: "DC No" },
  { field: "stage_label", header: "Stage" },
  { field: "part_no", header: "Part No." },
  { field: "part_name", header: "Part Name" },
  { field: "vendor_name", header: "Vendor Name" },
  { field: "qty", header: "Qty Pending", kind: "number" },
  { field: "issue_date", header: "Issue Date", kind: "date" },
  { field: "days_pending", header: "Days Pending", kind: "number" },
  { field: "work_order_code", header: "Work Order" },
  { field: "so_code", header: "Transfer Order" },
];

// Extra detail columns written to the Excel export only.
const EXPORT_EXTRA = [
  { field: "uom", header: "UOM" },
  { field: "ordered_qty", header: "Ordered / Sent / Issued Qty", kind: "number" },
  { field: "notes", header: "Notes / Batch" },
];

const SORTABLE = new Set([
  "alert_type", "ref_code", "dc_no", "stage_label", "part_no", "vendor_name",
  "qty", "issue_date", "days_pending", "work_order_code",
]);

const SUMMARY = [
  { key: "total_rows", label: "Total Pending Rows", icon: ListChecks, tone: "text-slate-700 bg-slate-100" },
  { key: "production_pending", label: "Production Pending", icon: ClipboardList, tone: "text-blue-700 bg-blue-50", alert: ALERT_PRODUCTION },
  { key: "job_work_overdue", label: "Job Work Overdue", icon: Truck, tone: "text-orange-700 bg-orange-50", alert: ALERT_JOB_WORK },
  { key: "stage_movement_delayed", label: "Stage Movement Delayed", icon: Timer, tone: "text-violet-700 bg-violet-50", alert: ALERT_STAGE },
  { key: "over_3_days", label: "Pending > 3 Days", icon: AlertTriangle, tone: "text-red-700 bg-red-50" },
  { key: "oldest_days", label: "Oldest (Days)", icon: Hourglass, tone: "text-amber-700 bg-amber-50" },
];

const EMPTY_FILTERS = {
  date_from: "", date_to: "", alert_type: "all", stage: "all", vendor: "", part: "", search: "",
};

const PAGE_SIZES = [25, 50, 100, 250];

function toParams(f) {
  const p = {};
  for (const k of ["date_from", "date_to", "vendor", "part", "search"]) {
    if (f[k] && String(f[k]).trim()) p[k] = String(f[k]).trim();
  }
  if (f.alert_type && f.alert_type !== "all") p.alert_type = f.alert_type;
  if (f.stage && f.stage !== "all") p.stage = f.stage;
  return p;
}

function rowTone(row) {
  if (row.highlight === "red") return "bg-red-50 hover:bg-red-100/70 border-l-4 border-l-red-500";
  if (row.highlight === "amber") return "bg-amber-50 hover:bg-amber-100/70 border-l-4 border-l-amber-400";
  return "hover:bg-slate-50/70 border-l-4 border-l-transparent";
}

function exportExcel(rows, summary) {
  const cols = [...PPO_COLUMNS, ...EXPORT_EXTRA];
  const cell = (r, c) => (
    c.kind === "number" ? Number(r[c.field] || 0) : c.kind === "date" ? fmtDate(r[c.field]) : r[c.field] ?? ""
  );
  const s = summary || {};
  const aoa = [
    cols.map((c) => c.header),
    ...rows.map((r) => cols.map((c) => cell(r, c))),
    [],
    ["Summary"],
    ["Total Pending Rows", Number(s.total_rows || 0)],
    ["Production Pending", Number(s.production_pending || 0)],
    ["Job Work Overdue", Number(s.job_work_overdue || 0)],
    ["Stage Movement Delayed", Number(s.stage_movement_delayed || 0)],
    [`Pending > ${HIGHLIGHT_DAYS} Days`, Number(s.over_3_days || 0)],
    ["Total Qty Pending", Number(s.total_pending_qty || 0)],
    ["Oldest (Days)", Number(s.oldest_days || 0)],
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = cols.map((c) => ({ wch: ["part_name", "alert_type", "stage_label", "notes"].includes(c.field) ? 26 : 16 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Pending Production");
  XLSX.writeFile(wb, `pending_production_orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function SortHeader({ col, sortBy, sortDir, onSort }) {
  const numeric = col.kind === "number";
  const sortable = SORTABLE.has(col.field);
  const active = sortBy === col.field;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 whitespace-nowrap ${numeric ? "text-right" : "text-left"}`}>
      {sortable ? (
        <button type="button" onClick={() => onSort(col.field)} className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? "text-slate-900" : ""}`} data-testid={`ppo-sort-${col.field}`}>
          {col.header}
          <Icon className={`w-3 h-3 ${active ? "" : "opacity-40"}`} />
        </button>
      ) : col.header}
    </th>
  );
}

export default function PendingProductionReport() {
  const [form, setForm] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [sortBy, setSortBy] = useState("days_pending");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [tick, setTick] = useState(0);

  // Keep the report in step with Production Flow / Job Work DCs / GINs.
  useEffect(() => {
    const refresh = () => setTick((t) => t + 1);
    const id = setInterval(refresh, REFRESH_MS);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(id); window.removeEventListener("focus", refresh); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get("/reports/pending-production", {
      params: { ...toParams(applied), sort_by: sortBy, sort_dir: sortDir, page, page_size: pageSize },
    })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(formatApiError(err?.response?.data?.detail)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [applied, sortBy, sortDir, page, pageSize, tick]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const runSearch = (e) => {
    e?.preventDefault();
    if (form.date_from && form.date_to && form.date_from > form.date_to) {
      toast.error("Date From must be on or before Date To");
      return;
    }
    setPage(1);
    setApplied({ ...form });
  };

  // Alert Type applies straight away (dropdown or summary card) — it's the
  // main way to split Job Work Overdue / Stage Movement Delayed rows out.
  const applyAlertType = (v) => {
    setForm((f) => ({ ...f, alert_type: v }));
    setApplied((f) => ({ ...f, alert_type: v }));
    setPage(1);
  };

  const reset = () => {
    setForm(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
    setSortBy("days_pending");
    setSortDir("desc");
  };

  const onSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDir(field === "issue_date" || field === "days_pending" || field === "qty" ? "desc" : "asc"); }
    setPage(1);
  };

  const refreshNow = useCallback(() => setTick((t) => t + 1), []);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/pending-production", {
        params: { ...toParams(applied), sort_by: sortBy, sort_dir: sortDir, page_size: 0 },
      });
      const all = res.data.rows || [];
      if (!all.length) {
        toast.error("No rows to export — adjust or clear the filters first.");
        return;
      }
      exportExcel(all, res.data.summary);
      toast.success(`Exported ${all.length} rows to Excel`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const rows = useMemo(() => data?.rows || [], [data]);
  const total = data?.total_rows || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const vendors = data?.vendors || [];
  const stages = data?.stages || [];
  const th = data?.thresholds || { job_work_days: 5, stage_days: 3, highlight_days: 3, red_days: 7 };

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const blank = <span className="text-slate-300">—</span>;

  const renderCell = (row, col) => {
    const v = row[col.field];
    switch (col.field) {
      case "alert_type":
        return <Badge className={`${ALERT_TONE[v] || "bg-slate-100 text-slate-700"} font-medium border-0 whitespace-nowrap`}>{v}</Badge>;
      case "ref_code":
        if (!v) return blank;
        return (
          <Link to={row.ref_kind === "work_order" ? "/work-orders" : "/production-flow"} className="mono font-semibold text-blue-700 hover:underline" data-testid={`ppo-ref-link-${row.id}`}>
            {v}
          </Link>
        );
      case "dc_no":
        return v ? <Link to="/delivery-challans" className="mono text-blue-700 hover:underline">{v}</Link> : blank;
      case "part_no":
      case "work_order_code":
      case "so_code":
        return v ? <span className="mono">{v}</span> : blank;
      case "part_name":
        return v ? <span className="block max-w-xs whitespace-normal">{v}</span> : blank;
      case "issue_date":
        return v ? fmtDate(v) : blank;
      case "days_pending":
        return (
          <span className={`font-semibold ${row.highlight === "red" ? "text-red-700" : row.highlight === "amber" ? "text-amber-700" : "text-slate-700"}`}>
            {fmtQty(v)}
          </span>
        );
      case "qty":
        return <span>{fmtQty(v)}{row.uom ? <span className="text-slate-400 ml-1">{row.uom}</span> : null}</span>;
      default:
        if (col.kind === "number") return fmtQty(v);
        return v || blank;
    }
  };

  const activeAlert = applied.alert_type;

  return (
    <div className="space-y-5" data-testid="pending-production-report-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Reports</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Factory className="w-7 h-7 text-blue-600" /> Pending Production Orders
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Open Work Orders, Job Work DCs not received back within {th.job_work_days} days of the DC date, and stage
            issues not moved forward within {th.stage_days} days — pulled live and refreshed automatically. Rows pending
            over {th.highlight_days} days are highlighted <span className="text-amber-700 font-medium">amber</span>, over {th.red_days} days <span className="text-red-700 font-medium">red</span>.
          </p>
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-2">
          {data?.generated_at && <span data-testid="ppo-updated">Updated {new Date(data.generated_at).toLocaleTimeString("en-IN")}</span>}
          <Button type="button" size="sm" variant="outline" onClick={refreshNow} disabled={loading} data-testid="ppo-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={runSearch} className="bg-white border border-slate-200 rounded-lg p-4" data-testid="ppo-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-slate-600">Alert Type</Label>
            <Select value={form.alert_type} onValueChange={applyAlertType}>
              <SelectTrigger className="h-9 mt-1" data-testid="ppo-alert-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All alert types</SelectItem>
                {ALERT_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Stage</Label>
            <Select value={form.stage} onValueChange={set("stage")}>
              <SelectTrigger className="h-9 mt-1" data-testid="ppo-stage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stages</SelectItem>
                {stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="ppo-date-from" className="text-xs text-slate-600">Issue Date From</Label>
            <Input id="ppo-date-from" type="date" value={form.date_from} onChange={(e) => set("date_from")(e.target.value)} className="h-9 mt-1" data-testid="ppo-date-from" />
          </div>
          <div>
            <Label htmlFor="ppo-date-to" className="text-xs text-slate-600">Issue Date To</Label>
            <Input id="ppo-date-to" type="date" value={form.date_to} onChange={(e) => set("date_to")(e.target.value)} className="h-9 mt-1" data-testid="ppo-date-to" />
          </div>
          <div>
            <Label htmlFor="ppo-vendor" className="text-xs text-slate-600">Vendor</Label>
            <Input id="ppo-vendor" list="ppo-vendor-options" value={form.vendor} onChange={(e) => set("vendor")(e.target.value)} placeholder="Job work vendor" className="h-9 mt-1" data-testid="ppo-vendor" />
            <datalist id="ppo-vendor-options">
              {vendors.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <Label htmlFor="ppo-part" className="text-xs text-slate-600">Part</Label>
            <Input id="ppo-part" value={form.part} onChange={(e) => set("part")(e.target.value)} placeholder="Part number or name" className="h-9 mt-1" data-testid="ppo-part" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="ppo-search" className="text-xs text-slate-600">Search</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="ppo-search" value={form.search} onChange={(e) => set("search")(e.target.value)} placeholder="Any Job / WO / DC no., part, vendor or transfer order" className="h-9 pl-8" data-testid="ppo-search" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 mt-4">
          <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="ppo-search-btn">
            <Search className="w-3.5 h-3.5 mr-1.5" /> Search
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={reset} data-testid="ppo-reset-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={doExport} disabled={exporting} data-testid="ppo-export-excel">
            {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}
            Export to Excel
          </Button>
        </div>
      </form>

      {/* Summary — counts over the filtered view; alert cards toggle the Alert Type filter. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ppo-summary">
        {SUMMARY.map(({ key, label, icon: Icon, tone, alert }) => {
          const selected = alert && activeAlert === alert;
          const body = (
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 leading-tight">{label}</div>
                <div className="mt-1.5 text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
                  {fmtQty(data?.summary?.[key] ?? 0)}
                </div>
              </div>
              <div className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center ${tone}`}>
                <Icon className="w-4 h-4" />
              </div>
            </div>
          );
          return alert ? (
            <button
              key={key}
              type="button"
              onClick={() => applyAlertType(selected ? "all" : alert)}
              className={`kpi-card !p-4 text-left transition ${selected ? "ring-2 ring-blue-500" : "hover:ring-1 hover:ring-slate-300"}`}
              title={selected ? "Show all alert types" : `Show only ${alert}`}
              data-testid={`ppo-summary-${key}`}
            >
              {body}
            </button>
          ) : (
            <div key={key} className="kpi-card !p-4" data-testid={`ppo-summary-${key}`}>{body}</div>
          );
        })}
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2">{error}</div>
      )}

      <div className="relative border border-slate-200 rounded-lg bg-white overflow-x-auto scroll-thin" data-testid="ppo-table-wrap">
        {loading && !data && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}
        <table className="min-w-full text-xs" data-testid="ppo-table">
          <thead className="bg-slate-50">
            <tr>
              {PPO_COLUMNS.map((col) => (
                <SortHeader key={col.field} col={col} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={`border-b border-slate-100 align-top ${rowTone(row)}`} data-testid={`ppo-row-${row.id}`} data-highlight={row.highlight || "none"}>
                {PPO_COLUMNS.map((col) => (
                  <td key={col.field} className={`px-3 py-2 whitespace-nowrap text-slate-700 ${col.kind === "number" ? "text-right tabular-nums" : ""}`}>
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={PPO_COLUMNS.length} className="text-center py-16 text-slate-400">
                  Nothing is pending for the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
        <div data-testid="ppo-row-count">
          Showing {rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} row{total === 1 ? "" : "s"}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Rows per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-20 h-8 text-xs" data-testid="ppo-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page <= 1} onClick={() => setPage(1)} data-testid="ppo-page-first">«</Button>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="ppo-page-prev">‹</Button>
            <span className="text-xs px-2">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="ppo-page-next">›</Button>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="ppo-page-last">»</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
